import React from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const SecurityContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#0B0813',
  padding: '40px 20px',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 'bold',
  color: '#00F0FF',
  fontFamily: '"Press Start 2P", cursive',
  marginBottom: '30px',
  textTransform: 'uppercase',
  letterSpacing: '2px',
  textShadow: '0 0 10px rgba(0, 240, 255, 0.5)',
}));

const ContentCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'rgba(11, 8, 19, 0.8)',
  border: '1px solid #00F0FF',
  borderRadius: '5px',
  boxShadow: '0 0 15px rgba(0, 240, 255, 0.1)',
  marginBottom: '20px',
}));

const ContentText = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '14px',
  lineHeight: '1.8',
  marginBottom: '15px',
}));

const HighlightText = styled(Typography)(({ theme }) => ({
  color: '#39FF14',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '14px',
  fontWeight: 'bold',
}));

const WarningText = styled(Typography)(({ theme }) => ({
  color: '#FF007A',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '14px',
  fontWeight: 'bold',
}));

const Security: React.FC = () => {
  return (
    <SecurityContainer>
      <Container maxWidth="lg">
        <SectionTitle>Security Architecture</SectionTitle>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ContentCard>
              <CardContent>
                <ContentText>
                  <HighlightText>// ENCRYPTION BEFORE EMBEDDING</HighlightText>
                </ContentText>
                <ContentText>
                  Files are encrypted using AES-256-GCM before being embedded into images. This ensures that even if someone detects the steganography, they cannot access your message without the encryption key.
                </ContentText>
                <ContentText>
                  • AES-256-GCM: Military-grade encryption<br/>
                  • PBKDF2 key derivation with 10,000 iterations<br/>
                  • Unique salt for each encryption<br/>
                  • Authenticated encryption with GCM mode
                </ContentText>
              </CardContent>
            </ContentCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ContentCard>
              <CardContent>
                <ContentText>
                  <HighlightText>// NO PERMANENT STORAGE</HighlightText>
                </ContentText>
                <ContentText>
                  No uploaded image is stored permanently on our servers. All processing happens in real-time, and images are immediately deleted after the encoding/decoding process completes.
                </ContentText>
                <ContentText>
                  • Images processed in memory only<br/>
                  • No database storage of user data<br/>
                  • No logging of image content<br/>
                  • Automatic cleanup after each request
                </ContentText>
              </CardContent>
            </ContentCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ContentCard>
              <CardContent>
                <ContentText>
                  <HighlightText>// SECURE PROCESSING</HighlightText>
                </ContentText>
                <ContentText>
                  Processing happens securely with optimized memory management and strict security controls:
                </ContentText>
                <ContentText>
                  • Memory limits to prevent data leakage<br/>
                  • Input validation and sanitization<br/>
                  • CORS protection for API endpoints<br/>
                  • Size limits on uploaded files (50MB max)<br/>
                  • Optimized algorithms to prevent timing attacks
                </ContentText>
              </CardContent>
            </ContentCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ContentCard>
              <CardContent>
                <ContentText>
                  <WarningText>// USER RESPONSIBILITY</WarningText>
                </ContentText>
                <ContentText>
                  While we provide robust security, users must also follow best practices:
                </ContentText>
                <ContentText>
                  • Use strong, unique passwords (min 8 characters)<br/>
                  • Include uppercase, lowercase, and numbers<br/>
                  • Never share your encryption keys<br/>
                  • Verify the recipient's identity<br/>
                  • Use secure channels for key exchange
                </ContentText>
              </CardContent>
            </ContentCard>
          </Grid>

          <Grid item xs={12}>
            <ContentCard>
              <CardContent>
                <ContentText>
                  <HighlightText>// OPEN SOURCE TRANSPARENCY</HighlightText>
                </ContentText>
                <ContentText>
                  Our code is open source, allowing security researchers to verify our implementations. We believe in transparency as a fundamental security principle. You can review our source code on GitHub to ensure our security claims are accurate.
                </ContentText>
                <ContentText>
                  • Public GitHub repository<br/>
                  • Regular security audits<br/>
                  • Community feedback integration<br/>
                  • Dependency vulnerability scanning
                </ContentText>
              </CardContent>
            </ContentCard>
          </Grid>
        </Grid>
      </Container>
    </SecurityContainer>
  );
};

export default Security;
